/**
 * useDragSelect Hook
 * Handles rubber band (marquee) selection in 'move' mode
 * Drag on blank area draws a selection rectangle, selects all elements within it.
 *
 * Shift+drag: 박스 안에서 layer 가 'l-lightingline' 인 cell 만 선택 (조명 라인 일괄 편집용).
 */

import { useEffect, useRef, RefObject } from 'react'
import { dia } from '@joint/core'
import { EditorTool } from '../../../map-editor-shared/store/uiStore'

const LIGHTING_LAYER = 'l-lightingline'

export function useDragSelect(
  paper: dia.Paper | null,
  graph: dia.Graph | null,
  canvasRef: RefObject<HTMLDivElement>,
  currentTool: EditorTool,
  onSelectionChange: (elementIds: Set<string>) => void
) {
  const isDragging = useRef(false)
  const startPoint = useRef({ x: 0, y: 0 })
  const selectionBox = useRef<HTMLDivElement | null>(null)
  // pointerdown 시점의 shift 상태를 보관 — pointerup 까지 동일 모드 유지.
  // 드래그 중 shift 를 떼더라도 시작 시점의 의도(필터 적용 여부)를 따른다.
  const shiftAtDown = useRef(false)

  useEffect(() => {
    if (!paper || !graph || currentTool !== 'move') return

    const handleBlankPointerDown = (evt: dia.Event) => {
      isDragging.current = true
      startPoint.current = { x: evt.clientX || 0, y: evt.clientY || 0 }
      // dia.Event 는 일반 MouseEvent 와 동일 필드를 갖지만 타입 정의가 누락될 수 있어 unknown 캐스팅
      shiftAtDown.current = !!(evt as unknown as { shiftKey?: boolean }).shiftKey

      // Create selection rectangle element — shift 모드는 노란색으로 시각 구분.
      const box = document.createElement('div')
      box.style.position = 'fixed'
      const accent = shiftAtDown.current ? '#EAB308' : '#3B82F6' // amber-500 vs blue-500
      const fill = shiftAtDown.current ? 'rgba(234, 179, 8, 0.1)' : 'rgba(59, 130, 246, 0.1)'
      box.style.border = `1px solid ${accent}`
      box.style.backgroundColor = fill
      box.style.pointerEvents = 'none'
      box.style.zIndex = '10000'
      box.style.left = `${startPoint.current.x}px`
      box.style.top = `${startPoint.current.y}px`
      box.style.width = '0px'
      box.style.height = '0px'
      document.body.appendChild(box)
      selectionBox.current = box
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current || !selectionBox.current) return

      const x = Math.min(e.clientX, startPoint.current.x)
      const y = Math.min(e.clientY, startPoint.current.y)
      const w = Math.abs(e.clientX - startPoint.current.x)
      const h = Math.abs(e.clientY - startPoint.current.y)

      selectionBox.current.style.left = `${x}px`
      selectionBox.current.style.top = `${y}px`
      selectionBox.current.style.width = `${w}px`
      selectionBox.current.style.height = `${h}px`
    }

    const handleMouseUp = (e: MouseEvent) => {
      if (!isDragging.current || !paper || !graph) return

      // Remove selection rectangle
      if (selectionBox.current) {
        selectionBox.current.remove()
        selectionBox.current = null
      }

      isDragging.current = false

      // Calculate selection rectangle in screen coordinates
      const x1 = Math.min(e.clientX, startPoint.current.x)
      const y1 = Math.min(e.clientY, startPoint.current.y)
      const x2 = Math.max(e.clientX, startPoint.current.x)
      const y2 = Math.max(e.clientY, startPoint.current.y)

      // Tiny drag = click on blank area → clear selection
      if (Math.abs(x2 - x1) < 5 && Math.abs(y2 - y1) < 5) {
        onSelectionChange(new Set())
        return
      }

      // Convert screen rect to paper (world) coordinates
      const paperEl = paper.el
      const paperRect = paperEl.getBoundingClientRect()
      const scale = paper.scale().sx
      const translate = paper.translate()

      const worldX1 = (x1 - paperRect.left - translate.tx) / scale
      const worldY1 = (y1 - paperRect.top - translate.ty) / scale
      const worldX2 = (x2 - paperRect.left - translate.tx) / scale
      const worldY2 = (y2 - paperRect.top - translate.ty) / scale

      // Find all elements within the selection rectangle.
      // shiftAtDown.current=true → l-lightingline 레이어 cell 만 선택 (조명 일괄편집).
      const filterToLighting = shiftAtDown.current
      const selectedIds = new Set<string>()

      graph.getCells().forEach((cell) => {
        if (!cell.isElement()) return

        if (filterToLighting) {
          // cell.data.layer 는 elementFactory 가 설정. 없으면 제외.
          const data = cell.get('data') as { layer?: string } | undefined
          if (!data?.layer || data.layer !== LIGHTING_LAYER) return
        }

        const bbox = cell.getBBox()
        const cx = bbox.x + bbox.width / 2
        const cy = bbox.y + bbox.height / 2

        // Element center must be within selection rectangle
        if (cx >= worldX1 && cx <= worldX2 && cy >= worldY1 && cy <= worldY2) {
          selectedIds.add(cell.id as string)
        }
      })

      // shift 필터 모드에서는 0개여도 명시적으로 빈 선택을 emit (사용자가 의도적으로
      // 그 영역엔 조명이 없음을 보고 싶어할 수 있음 + 이전 선택 클리어 효과).
      if (selectedIds.size > 0 || filterToLighting) {
        onSelectionChange(selectedIds)
      }
    }

    paper.on('blank:pointerdown', handleBlankPointerDown)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      paper.off('blank:pointerdown', handleBlankPointerDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      // Cleanup any remaining selection box
      if (selectionBox.current) {
        selectionBox.current.remove()
        selectionBox.current = null
      }
    }
  }, [paper, graph, currentTool, onSelectionChange])
}
